import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as Sentry from "@sentry/react";
import { useSearchParams } from 'react-router-dom';
import { Search as SearchIcon, Filter, X, ChevronDown } from 'lucide-react';
import { getCommunityFilterFacets, searchCommunityPosts } from '../../services/api';
import PostCard from '../../components/community/PostCard';
import { CommunityError } from "../../errors/sentryErrors";

const Search = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get('q') || '';
  const initialDestination = searchParams.get('destination') || '';
  
  const [query, setQuery] = useState(initialQuery);
  const [activeFilters, setActiveFilters] = useState({
    destination: initialDestination,
    budget: searchParams.get('budget') || '',
    duration: searchParams.get('duration') || '',
    month: searchParams.get('month') || '',
    tripType: searchParams.get('tripType') || ''
  });
  const [sort, setSort] = useState(searchParams.get('sort') || 'recent');
  
  const [facets, setFacets] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [nextCursor, setNextCursor] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [error, setError] = useState(null);
  
  const observer = useRef();
  const lastElementRef = useCallback(node => {
    if (loading) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        loadMore();
      }
    });
    if (node) observer.current.observe(node);
  }, [loading, hasMore]);

  // Load facets once
  useEffect(() => {
    getCommunityFilterFacets()
      .then(res => setFacets(res))
      .catch((err) => {
        Sentry.withScope((scope) => {
          scope.setTag("feature", "community");
          scope.setTag("app", "web");
          scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
          Sentry.captureException(new CommunityError(err instanceof Error ? err.message : String(err)));
        });
      });
  }, []);

  const fetchResults = async (cursor = null, isReset = false) => {
    try {
      setLoading(true);
      setError(null);
      const params = {
        q: query,
        ...activeFilters,
        sort,
        limit: 12
      };
      if (cursor) params.cursor = cursor;
      
      const res = await searchCommunityPosts(params);
      const newItems = res.data || [];
      const cursorNext = res.meta?.nextCursor;
      
      setResults(prev => isReset ? newItems : [...prev, ...newItems]);
      setNextCursor(cursorNext);
      setHasMore(!!cursorNext && newItems.length > 0);
      
      // Update URL silently
      const newSearchParams = new URLSearchParams();
      if (query) newSearchParams.set('q', query);
      Object.entries(activeFilters).forEach(([k, v]) => {
        if (v) newSearchParams.set(k, v);
      });
      if (sort !== 'recent') newSearchParams.set('sort', sort);
      setSearchParams(newSearchParams, { replace: true });
      
    } catch (err) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "community");
        scope.setTag("app", "web");
        scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
        Sentry.captureException(new CommunityError(err instanceof Error ? err.message : String(err)));
      });
      // Was console-only, so a rejected filter (the API validates budget,
      // duration, tripType and month) looked like "no trips match".
      console.error("Search failed:", err);
      setError(err?.message || "Couldn't load results. Please try again.");
      if (isReset) setResults([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  };

  // Trigger search when filters/sort change or form is submitted
  useEffect(() => {
    setNextCursor(null);
    setHasMore(true);
    fetchResults(null, true);
  }, [activeFilters, sort]); // Note: NOT triggered on query change until submitted

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setNextCursor(null);
    setHasMore(true);
    fetchResults(null, true);
  };

  const loadMore = () => {
    if (!loading && hasMore) {
      fetchResults(nextCursor, false);
    }
  };

  const setFilter = (key, value) => {
    setActiveFilters(prev => ({ ...prev, [key]: value }));
  };

  const removeFilter = (key) => {
    setActiveFilters(prev => ({ ...prev, [key]: '' }));
  };

  const activeFilterCount = Object.values(activeFilters).filter(Boolean).length;

  // Chips showed the raw stored value — "month: 6", "tripType: solo". Resolve
  // them back through the facets so they read the way the dropdown did.
  const FILTER_LABELS = {
    destination: 'Destination',
    tripType: 'Trip type',
    budget: 'Budget',
    duration: 'Duration',
    month: 'Month',
  };
  const displayFilterValue = (key, value) => {
    if (key === 'month') {
      return facets?.months?.find(m => String(m.month) === String(value))?.label || value;
    }
    if (key === 'tripType') {
      return facets?.tripTypes?.find(t => t.type === value)?.label || value;
    }
    return value;
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-20">
      <div className="bg-white border-b border-gray-200 shadow-sm sticky top-16 z-30 pt-6 pb-4 mb-8">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          
          <div className="flex flex-col md:flex-row gap-3 mb-4">
            <button 
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center justify-center gap-2 px-5 py-2 border rounded-xl transition-colors text-gray-700 font-medium shrink-0
                ${showFilters ? 'bg-gray-900 text-white border-gray-900' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'}
              `}
            >
              <Filter size={18} className={showFilters ? 'text-white' : 'text-gray-500'} />
              <span className="hidden sm:inline">Filters</span>
            </button>

            <form onSubmit={handleSearchSubmit} className="relative flex-1">
              <div className="relative flex items-center bg-gray-50 border border-gray-200 rounded-xl p-1.5 pl-4 focus-within:bg-white focus-within:border-blue-500 focus-within:ring-4 focus-within:ring-blue-500/10 transition-all">
                <SearchIcon className="text-gray-400 mr-2 shrink-0" size={20} />
                <input 
                  type="text" 
                  placeholder="Search trips, destinations, experiences..." 
                  className="w-full bg-transparent text-gray-900 outline-none text-base py-1.5 min-w-0"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                />
                <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg font-semibold ml-2 shrink-0 transition-colors shadow-sm text-sm">
                  Search
                </button>
              </div>
            </form>
          </div>

          {/* Filters Bar */}
          {showFilters && (
            <div className="flex flex-wrap items-center gap-3 animate-in fade-in slide-in-from-top-2 duration-300">
              
              {/* Destination Dropdown */}
              <div className="relative group">
              <select 
                className="appearance-none bg-gray-50 border border-gray-200 text-gray-700 py-2 pl-4 pr-10 rounded-full text-sm font-medium focus:outline-none focus:border-blue-500 hover:bg-gray-100 cursor-pointer"
                value={activeFilters.destination}
                onChange={e => setFilter('destination', e.target.value)}
              >
                <option value="">Any Destination</option>
                {facets?.destinations?.map((d, i) => (
                  <option key={d.name || i} value={d.name}>{d.name} ({d.count})</option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>

            {/* Trip Type Dropdown */}
            <div className="relative group">
              <select 
                className="appearance-none bg-gray-50 border border-gray-200 text-gray-700 py-2 pl-4 pr-10 rounded-full text-sm font-medium focus:outline-none focus:border-blue-500 hover:bg-gray-100 cursor-pointer"
                value={activeFilters.tripType}
                onChange={e => setFilter('tripType', e.target.value)}
              >
                <option value="">Any Trip Type</option>
                {facets?.tripTypes?.map((t, i) => (
                  <option key={t.type || i} value={t.type || ''} disabled={t.count === 0}>
                    {t.label || t.type || 'Unknown'} ({t.count})
                  </option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>

            {/* Budget Dropdown */}
            <div className="relative group">
              <select 
                className="appearance-none bg-gray-50 border border-gray-200 text-gray-700 py-2 pl-4 pr-10 rounded-full text-sm font-medium focus:outline-none focus:border-blue-500 hover:bg-gray-100 cursor-pointer"
                value={activeFilters.budget}
                onChange={e => setFilter('budget', e.target.value)}
              >
                <option value="">Any Budget</option>
                {facets?.budgetBuckets?.map((b, i) => (
                  <option key={b.name || b.label || i} value={b.name || b.label}>{b.label}</option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>

            {/* Duration Dropdown */}
            <div className="relative group">
              <select 
                className="appearance-none bg-gray-50 border border-gray-200 text-gray-700 py-2 pl-4 pr-10 rounded-full text-sm font-medium focus:outline-none focus:border-blue-500 hover:bg-gray-100 cursor-pointer"
                value={activeFilters.duration}
                onChange={e => setFilter('duration', e.target.value)}
              >
                <option value="">Any Duration</option>
                {facets?.durations?.map((d, i) => (
                  <option key={d.label || i} value={d.label}>{d.label}</option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>

            {/* Month. The facets endpoint has always returned months and
                `activeFilters.month` was already wired into the query — the
                dropdown to set it was simply missing, so the filter could only be
                reached by hand-editing the URL. */}
            <div className="relative group">
              <select
                className="appearance-none bg-gray-50 border border-gray-200 text-gray-700 py-2 pl-4 pr-10 rounded-full text-sm font-medium focus:outline-none focus:border-blue-500 hover:bg-gray-100 cursor-pointer"
                value={activeFilters.month}
                onChange={e => setFilter('month', e.target.value)}
              >
                <option value="">Any Month</option>
                {facets?.months?.map((m, i) => (
                  <option key={m.month || i} value={m.month} disabled={m.count === 0}>
                    {m.label} ({m.count})
                  </option>
                ))}
              </select>
              <ChevronDown size={14} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>

            {/* Sort */}
            <div className="relative group ml-auto">
              <select 
                className="appearance-none bg-blue-50 border border-blue-100 text-blue-700 py-2 pl-4 pr-10 rounded-full text-sm font-bold focus:outline-none hover:bg-blue-100 cursor-pointer"
                value={sort}
                onChange={e => setSort(e.target.value)}
              >
                {/* Values must match searchService.ALLOWED_SORTS. This sent
                    "rating", which is not in that list, so the API fell back to
                    "recent" and the option appeared to do nothing. */}
                <option value="recent">Sort: Newest</option>
                <option value="popular">Sort: Popular</option>
                <option value="highest_rated">Sort: Highest Rated</option>
              </select>
              <ChevronDown size={14} className="absolute right-4 top-1/2 -translate-y-1/2 text-blue-500 pointer-events-none" />
            </div>
          </div>
          )}

          {/* Active Filter Chips */}
          {activeFilterCount > 0 && (
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-100">
              {Object.entries(activeFilters).map(([key, value]) => {
                if (!value) return null;
                return (
                  <div key={key} className="bg-gray-900 text-white text-xs font-medium px-3 py-1.5 rounded-lg flex items-center gap-2">
                    <span>{FILTER_LABELS[key] || key}:</span>
                    {displayFilterValue(key, value)}
                    <button onClick={() => removeFilter(key)} className="hover:text-red-400 focus:outline-none">
                      <X size={14} />
                    </button>
                  </div>
                );
              })}
              <button 
                onClick={() => setActiveFilters({ destination: '', budget: '', duration: '', month: '', tripType: '' })}
                className="text-sm text-gray-500 hover:text-gray-900 underline ml-2"
              >
                Clear all
              </button>
            </div>
          )}

        </div>
      </div>

      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Results Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {results.map((post, index) => {
            const isLast = results.length === index + 1;
            const ref = isLast ? lastElementRef : null;
            
            return (
              <div key={post.id || index} ref={ref}>
                <PostCard post={post} />
              </div>
            );
          })}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="aspect-[4/4.6] bg-gray-200 rounded-[24px] animate-pulse"></div>
            ))}
          </div>
        )}

        {/* A rejected request is not the same as "nothing matched" — say so, and
            offer a retry rather than sending the user to change filters that were
            never the problem. */}
        {!loading && error && (
          <div className="text-center py-20 px-6 max-w-md mx-auto">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Couldn&apos;t load results</h3>
            <p className="text-gray-500 mb-6">{error}</p>
            <button
              onClick={() => fetchResults(null, true)}
              className="bg-gray-900 hover:bg-gray-800 text-white px-6 py-2.5 rounded-xl font-medium transition-colors cursor-pointer"
            >
              Try again
            </button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && results.length === 0 && (
          <div className="text-center py-24 px-6 max-w-md mx-auto">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <SearchIcon size={40} className="text-gray-300" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">No trips found</h3>
            <p className="text-gray-500 mb-8">
              We couldn't find any trips matching your current filters. Try removing some filters or searching for something else.
            </p>
            <button 
              onClick={() => {
                setQuery('');
                setActiveFilters({ destination: '', budget: '', duration: '', month: '', tripType: '' });
              }}
              className="bg-gray-900 hover:bg-gray-800 text-white px-8 py-3 rounded-xl font-medium transition-colors"
            >
              Reset All Filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Search;
